import { useModal, DateField, Icon } from '@jutro/components';
import { BreakpointTrackerContext } from '@jutro/layout';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { messages as commonMessages } from '@xengage/gw-platform-translations';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import _ from 'lodash';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useDependencies } from '@xengage/gw-portals-dependency-react';
import messages from './ClaimDocumentUploadComponent.messages.js';
import metadata from './ClaimDocumentUploadComponent.metadata.json5';

function ClaimDocumentUploadComponent(props) {
    const breakpoint = useContext(BreakpointTrackerContext);
    const { showAlert } = useModal();
    const { claimVM } = props;
    const { authHeader } = useAuthentication();
    const [documentSearch, setDocumentSearch] = useState('');
    const [documents, setDocuments] = useState([]);
    const [displayDocuments, setDisplayDocuments] = useState([]);
    const [loading, setLoading] = useState(false);
    const { ClaimService } = useDependencies('ClaimService');
    const { ClaimDownloadService } = useDependencies('ClaimDownloadService');

    useEffect(() => {
        setDocumentSearch('');

        const documentsArray = _.get(claimVM, 'documents');

        setDocuments(documentsArray);
        setDisplayDocuments(documentsArray);
    }, []);

    const handleError = useCallback(
        (title, message) =>
            showAlert({
                title,
                message,
                status: 'error',
                icon: 'mi-error-outline',
                confirmButtonText: commonMessages.ok,
            }).catch(_.noop),
        [showAlert]
    );

    const onChangeDocumentSearch = useCallback(
        searchTerm => {
            if (searchTerm && documents && documents.length > 0) {
                const filtered = documents?.filter(doc => doc.name?.toLowerCase().includes(searchTerm?.toLowerCase()));

                setDisplayDocuments(filtered);
            } else {
                setDisplayDocuments(documents);
            }

            setDocumentSearch(searchTerm);
        },
        [setDocumentSearch, setDisplayDocuments, documents]
    );

    const getDocumentName = useCallback((item, index) => (
        item.name
    ), []);

    const getDocumentUploaded = useCallback((item, index) => (
        <DateField
            id={`uploadedDate${index}`}
            value={item.dateModified}
            readOnly
            displayFormat="vshort"
        />
    ), []);

    const getViewandDownload = useCallback((item, index) => (
        <Icon
            id={`update${index}`}
            onClick={() => onDownloadDocument(item)}
            size="medium"
            icon="gw-insert-drive-file"
            color="#26a9e0"
            style={{ justifyItems: 'left' }}
        />
    ), []);

    const onUploadDocument = useCallback(
        async file => {

            setLoading(true);

            const documentMetaDataTemplate = {
                DocUID: '001',
                DocumentType: 'fnol',
                SecurityType: 'unrestricted',
                Status: 'approved',
                Author: 'Policy Holder',
                claimNumber: claimVM.claimNumber,
                name: file.name,
                mimeType: file.type,
                sessionID: "-"
            };

            try {
                const data = await ClaimService.uploadDocument(
                    file,
                    documentMetaDataTemplate,
                    authHeader
                );
                const documentsArray = _.get(claimVM, 'documents');

                documentsArray.push(data);
                setDocuments(documentsArray);
                setDisplayDocuments(documentsArray);
                setLoading(false);
            } catch (error) {
                setLoading(false);
                handleError(
                    commonMessages.errorUploadTitle,
                    commonMessages.uploadFailedMessage
                );
            }
        },
        [claimVM, ClaimService, authHeader, handleError]
    );

    // generate download token before downloading document
    const onDownloadDocument = useCallback(async (aDocument) => {
        setLoading(true);

        try {
            const policyNumber = _.get(claimVM, 'policy.policyNumber');
            const docId = aDocument.docUID;
            const token = await ClaimService.claimsDocDownloadToken(
                [policyNumber, docId],
                authHeader
            );
            const downloadURL = ClaimDownloadService.getClaimDocument(
                docId,
                token
            );

            window.location.href = `${downloadURL}&claim=${claimVM.claimNumber}`;
        } catch (e) {
            console.log(`error downloading ${e}`);
            handleError(
                commonMessages.errorUploadTitle,
                messages.downloadError
            );
        } finally {
            setLoading(false);
        }
    }, [claimVM, ClaimService, authHeader, ClaimDownloadService, handleError]);

    const overrideProps = {
        '@field': {
            labelPosition: breakpoint === 'desktop' ? 'left' : 'top',
        },
        documentUploadContainer: {
            visible: !loading
        },
        loadingIndicator: {
            visible: loading
        },
        documentSearch: {
            visible: documents && documents.length > 0,
            value: documentSearch
        },
        documentsTableGrid: {
            data: displayDocuments,
            visible: displayDocuments.length > 0,
        },
        emptyDocmsg: {
            visible: displayDocuments?.length === 0,
        },
    };

    const resolvers = {
        resolveClassNameMap: {},
        resolveComponentMap: {},
        resolveCallbackMap: {
            onUploadDocument,
            onChangeDocumentSearch,
            getDocumentName,
            getDocumentUploaded,
            getViewandDownload
        },
    };

    return (
        <ViewModelForm
            uiProps={metadata.pageContent}
            overrideProps={overrideProps}
            callbackMap={resolvers.resolveCallbackMap}
            componentMap={resolvers.resolveComponentMap}
            classNameMap={resolvers.resolveClassNameMap}
        />
    );
}

export default ClaimDocumentUploadComponent;
